import { useMemo, useState } from "react";
import { api, fmtMoney } from "../lib/api";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { ChartLineUp, EnvelopeSimple, CurrencyEur } from "@phosphor-icons/react";

const MONTHS = [
  "janvier", "février", "mars", "avril", "mai", "juin",
  "juillet", "août", "septembre", "octobre", "novembre", "décembre",
];

export default function MonthlyBusinessRecapDialog({ open, onOpenChange }) {
  const now = new Date();
  // Default = previous month (natural "fin de mois" behavior)
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const [year, setYear] = useState(prev.getFullYear());
  const [month, setMonth] = useState(prev.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const monthLabel = useMemo(() => `${MONTHS[month - 1]} ${year}`, [month, year]);

  const preview = async () => {
    setLoading(true);
    setData(null);
    try {
      const r = await api.post("/reports/monthly-business", { year, month, send: false });
      setData(r.data.recap);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  const send = async () => {
    setSending(true);
    try {
      const r = await api.post("/reports/monthly-business", { year, month, send: true });
      setData(r.data.recap);
      if (r.data.email_sent) {
        toast.success(`Récap ${monthLabel} envoyé à letantra972@gmail.com`);
      } else {
        toast.error("Le récap a été généré mais l'email n'a pas pu être envoyé");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'envoi");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121217] border-[#27272a] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <ChartLineUp size={22} color="#fbbf24" weight="fill" />
            Récap de fin de mois
          </DialogTitle>
          <DialogDescription className="text-[#a1a1aa]">
            CA total + règlements par danseuse + estimation boissons/autres. Envoyé par email au patron.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="label-xs">Mois</Label>
            <select
              data-testid="recap-month"
              value={month}
              onChange={(e) => { setMonth(Number(e.target.value)); setData(null); }}
              className="w-full bg-[#09090b] border border-[#27272a] text-white rounded-md px-3 py-2 h-10 text-sm capitalize"
            >
              {MONTHS.map((m, i) => (
                <option key={i + 1} value={i + 1}>{m}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="label-xs">Année</Label>
            <Input
              data-testid="recap-year"
              type="number"
              value={year}
              onChange={(e) => { setYear(Number(e.target.value)); setData(null); }}
              className="bg-[#09090b] border-[#27272a] h-10"
            />
          </div>
        </div>

        {!data && (
          <div className="text-center py-8 text-[#71717a] text-sm">
            Cliquez sur <b className="text-white">Prévisualiser</b> pour voir les chiffres avant envoi.
          </div>
        )}

        {data && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Kpi label="CA total" value={fmtMoney(data.total_ca)} color="#fbbf24" big />
              <Kpi label="CA Danseuses" value={fmtMoney(data.total_ca_danseuses)} color="#fbbf24" />
              <Kpi label="CA Boissons" value={fmtMoney(data.total_boissons_autre)} color={data.total_boissons_autre >= 0 ? "#34d399" : "#f87171"} />
              <Kpi label="Net caisse" value={fmtMoney(data.total_net_cash)} color={data.total_net_cash >= 0 ? "#34d399" : "#f87171"} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <Kpi label="Dû aux danseuses" value={fmtMoney(data.total_du_danseuses)} color="#fbbf24" />
              <Kpi label="Déjà payé" value={fmtMoney(data.total_paye_danseuses)} color="#34d399" />
              <Kpi label="Reste à payer" value={fmtMoney(data.total_reste_danseuses)} color={data.total_reste_danseuses > 0 ? "#f87171" : "#a1a1aa"} />
            </div>

            <div className="p-4 rounded-md border border-[#27272a] bg-[#09090b]">
              <div className="flex items-center gap-2 mb-1">
                <CurrencyEur size={16} color="#34d399" />
                <div className="text-xs text-[#a1a1aa]">CA Boissons / Reste</div>
              </div>
              <div className="text-xs text-[#71717a] mb-2">CA total − CA généré par les danseuses (billets + jetons)</div>
              <div
                data-testid="recap-estimation"
                className="font-display text-3xl font-light"
                style={{ color: data.total_boissons_autre >= 0 ? "#34d399" : "#f87171" }}
              >
                {fmtMoney(data.total_boissons_autre)}
              </div>
            </div>

            <div>
              <div className="label-xs mb-2">
                Détail par danseuse ({data.per_dancer.length} danseuse{data.per_dancer.length > 1 ? "s" : ""})
              </div>
              <div className="border border-[#27272a] rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#09090b]">
                    <tr className="text-[#71717a] text-[11px] uppercase">
                      <th className="text-left p-2">Nom de scène</th>
                      <th className="text-right p-2">CA généré</th>
                      <th className="text-right p-2">Dû</th>
                      <th className="text-right p-2">Payé</th>
                      <th className="text-right p-2">Reste</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.per_dancer.length === 0 ? (
                      <tr><td colSpan={5} className="p-4 text-center text-[#71717a]">Aucune activité sur la période</td></tr>
                    ) : (
                      data.per_dancer.map((d) => (
                        <tr key={d.dancer_id} className="border-t border-[#27272a]">
                          <td className="p-2">
                            <div>{d.nom_scene}</div>
                            {d.nom_civil && <div className="text-[11px] text-[#71717a]">{d.nom_civil}</div>}
                          </td>
                          <td className="p-2 text-right font-mono text-[#fbbf24]">{fmtMoney(d.ca_genere)}</td>
                          <td className="p-2 text-right text-[#a1a1aa]">{fmtMoney(d.total_du)}</td>
                          <td className="p-2 text-right text-[#34d399]">{fmtMoney(d.total_paye)}</td>
                          <td className="p-2 text-right font-mono" style={{ color: d.reste_a_payer > 0 ? "#f87171" : "#a1a1aa" }}>{fmtMoney(d.reste_a_payer)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-[#27272a] text-white hover:bg-[#1a1a21]"
          >
            Fermer
          </Button>
          <Button
            onClick={preview}
            disabled={loading}
            variant="outline"
            className="border-[#fbbf24] text-[#fbbf24] hover:bg-[#fbbf24]/10"
            data-testid="recap-preview"
          >
            {loading ? "Calcul..." : "Prévisualiser"}
          </Button>
          <Button
            onClick={send}
            disabled={sending}
            className="bg-[#fbbf24] hover:bg-[#f59e0b] text-black"
            data-testid="recap-send"
          >
            <EnvelopeSimple size={16} className="mr-2" weight="fill" />
            {sending ? "Envoi..." : "Envoyer au patron"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Kpi({ label, value, color, big = false }) {
  return (
    <div className="p-3 rounded-md border border-[#27272a] bg-[#09090b]">
      <div className="label-xs">{label}</div>
      <div className={`font-display font-light tracking-tight mt-1 ${big ? "text-2xl" : "text-lg"}`} style={{ color }}>
        {value}
      </div>
    </div>
  );
}
